import React, {useEffect, useState} from 'react';

import './styles.scss';
import {Column, Row} from '../layout';
import {useHistory} from 'react-router-dom';
import * as fonts from '../../util/fonts';
import {createGUID} from '../../util/utility';
import Icon from '../icon';
import {Icons} from '../../util/icons';
import colors from '../../util/colors';
import {compose} from 'redux';
import {connect} from 'react-redux';
import AppLanguageSelector from '../app-language-selector';
import {strings} from '../../util/strings';
import {ITenant} from '../../App';
import AuthorizedImage from '../images';
import {MenuItem} from '../app-nav-menu/types';
import {AuthConsumer} from '../../providers/AuthProvider';
import LogoutIcon from '@mui/icons-material/Logout';
import {trackMenuClick} from '../../util/appInsights';
import {
  useDropdownState,
  useNavSelection,
  useVisibleMenuItems,
} from '../app-nav-menu/hooks';
interface AppNavResponsiveProps {
  items: MenuItem[];
  onClose(): void;
  busFuncMap: any;
  selectedBu: any;
  buStyles: any;
  user: any;
  labels: typeof strings.pt.menu;
  tenant: ITenant;
}

function AppNavResponsive({
  items,
  onClose,
  busFuncMap,
  selectedBu,
  buStyles,
  user,
  labels,
  tenant,
}: AppNavResponsiveProps) {
  const history = useHistory();
  const visibleItems = useVisibleMenuItems(items, busFuncMap, selectedBu, user);
  const textStyle = {
    ...fonts.L,
    ...fonts.SemiBold,
    color: buStyles?.primaryColor || colors.fluorescentBlue,
  };

  const {currentPath, handleNavigate} = useNavSelection(history);

  const initialOpenItem = items.find((item) =>
    item.subItems?.some((sub) => sub.url === currentPath),
  )?.funct;

  const {setSelectedItem, toggleItem, isOpen} =
    useDropdownState(initialOpenItem);

  const [showLanguageSelector, setShowLanguageSelector] = useState(false);

  const allLabels = {...strings.pt.menu, ...labels};

  useEffect(() => {
    const newInitialOpenItem = items.find((item) =>
      item.subItems?.some((sub) => sub.url === currentPath),
    )?.funct;

    setSelectedItem(newInitialOpenItem || null);
  }, [currentPath, items, setSelectedItem]);

  const renderMenuItem = (item: MenuItem) => {
    const isParentActive = item.subItems?.some(
      (subItem) => subItem.url === currentPath,
    );
    const isDirectActive = item.url === currentPath;

    const isItemOpen = isOpen(item.funct);

    const activeItemStyle = (isActive: boolean | undefined) => ({
      borderBottom: isActive
        ? `solid 3px ${buStyles?.primaryColor || colors.fluorescentBlue}`
        : 'none',
    });

    return (
      <Column key={createGUID()}>
        {item.url === '' ? (
          <Row
            className="menu-item-container"
            onClick={() => toggleItem(item.funct)}>
            <Column
              className="app-nav-menu-label"
              key={item.label}
              style={textStyle}>
              <span style={activeItemStyle(isDirectActive || isParentActive)}>
                {item.label}
              </span>
            </Column>
            <Column className="logout-user-column-dropdown dropdown-inner">
              <Icon
                className={`logout-user-input-icon${
                  isItemOpen ? '-dropdown' : ''
                }`}
                color={buStyles?.primaryColor || colors.fluorescentBlue}
                name={Icons.CHEVRON_DOWN}
                width={'22'}
                height={'22'}
                strokeWidth={'1'}
              />
            </Column>
          </Row>
        ) : (
          <Row
            className="app-nav-menu-label"
            key={item.label}
            onClick={() => {
              trackMenuClick(item.label, item.url, {
                menuLevel: 'responsive_root',
                funct: item.funct,
                buId: selectedBu?.buId,
              });
              handleNavigate(item.url, item.funct);
              toggleItem(item.funct);
              onClose();
            }}
            style={textStyle}>
            <span style={activeItemStyle(isDirectActive || isParentActive)}>
              {item.label}
            </span>
          </Row>
        )}

        {isItemOpen && (
          <div className="app-nav-menu-dropdown">
            {item.subItems?.map((subItem) =>
              busFuncMap
                ? busFuncMap[selectedBu?.buId]?.map((func: any) => {
                    if (
                      func?.isVisible &&
                      subItem.funct === func?.featureTemplateAlias
                    ) {
                      const isSubActive = subItem.url === currentPath;

                      return (
                        <div key={createGUID()}>
                          <div
                            key={subItem.label}
                            className="app-nav-menu-sub-item"
                            onClick={() => {
                              trackMenuClick(subItem.label, subItem.url, {
                                menuLevel: 'responsive_sub',
                                parent: item.label,
                                funct: subItem.funct,
                                buId: selectedBu?.buId,
                              });
                              handleNavigate(subItem.url, item.funct);
                              onClose();
                            }}
                            style={{
                              ...fonts.ML,
                              ...fonts.SemiBold,
                              color:
                                buStyles?.primaryColor ||
                                colors.fluorescentBlue,
                            }}>
                            <span style={activeItemStyle(isSubActive)}>
                              {subItem.label}
                            </span>
                          </div>
                        </div>
                      );
                    } else {
                      return null;
                    }
                  })
                : null,
            )}
          </div>
        )}
      </Column>
    );
  };

  const hasAtLeastOneChildActive = (item: any) => {
    const subItemsSelectors = item.subItems.map(
      (subItem: any) => subItem.funct,
    );
    return busFuncMap[selectedBu?.buId].some((buFunc: any) =>
      subItemsSelectors.includes(buFunc.featureTemplateAlias),
    );
  };

  return (
    <div className="app-nav-responsive">
      <AuthorizedImage
        requiresAuthorization={false}
        source={`${process.env.REACT_APP_BASE_BACKOFFICE_IMAGES_URL_PUBLIC}${
          tenant?.footerImage || buStyles?.logo
        }`}
        alt="ADVLogo"
        width="210"
        height="72"
        className="app-header-logo app-header-logo-responsive"
      />
      <Icon
        className="responsive-menu-button close-button svg-close-button"
        width="26"
        height="26"
        name={Icons.CLOSE}
        onClick={() => onClose()}
      />
      <div className="app-nav-menu-row-container">
        <Row className="app-nav-menu-row">
          {visibleItems?.map((item) =>
            busFuncMap
              ? busFuncMap[selectedBu?.buId]?.map((func: any) => {
                  if (
                    func?.isVisible &&
                    item.funct === func?.featureTemplateAlias &&
                    !item.subItems
                  ) {
                    return renderMenuItem(item);
                  } else if (
                    func?.isVisible &&
                    item.funct === func?.featureTemplateAlias &&
                    item.subItems &&
                    item.subItems.length > 0 &&
                    hasAtLeastOneChildActive(item)
                  ) {
                    return renderMenuItem(item);
                  } else {
                    return null;
                  }
                })
              : null,
          )}
          <Row
            className="app-nav-menu-label"
            onClick={() => {
              handleNavigate('/perfil', 'myProfile');
              onClose();
            }}
            style={{...textStyle, display: 'flex'}}>
            <span>{allLabels?.menu_user__submenu_profile}</span>
          </Row>
        </Row>
        <AuthConsumer>
          {({logout}) => (
            <div className="app-nav-menu-logout" onClick={logout}>
              <LogoutIcon sx={{color: buStyles?.primaryColor}} />
              <span
                style={{...fonts.ML, ...fonts.SemiBold, color: colors.black}}>
                {allLabels?.menu_button_logout}
              </span>
            </div>
          )}
        </AuthConsumer>
      </div>
      {user.state === 'CLIENT' && (
        <AppLanguageSelector
          show={showLanguageSelector}
          setShow={setShowLanguageSelector}
        />
      )}
    </div>
  );
}

const mapStateToProps = ({globalStyles, user, strings, tenant}: any) => ({
  buStyles: globalStyles.value,
  user: user.value,
  labels: strings.value.pt.menu,
  tenant: tenant.value,
});

export default compose(connect(mapStateToProps)(AppNavResponsive));
